import { Container, Graphics, LINE_CAP, Text } from "pixi.js"
import { TEXT_ROLES } from "../text/typography"
import { TextRenderer } from "../text/text_renderer"

// The HUD badge shown while an ember camp is under assault: which wave is being fought and how many
// of its enemies are still to be put down.
//
// It deliberately takes the pre-wave COUNTDOWN DIAL's slot and its shape — same radius, same stroke,
// same dark face and groove. The countdown drains to zero, disappears, and this dial takes its place
// already full, so the HUD reads as one object handing over: "time until the wave" becomes "what's
// left of the wave". The palettes are what separate them: the countdown ramps green->amber->red as
// safety runs out, this one burns down from hostile red to ember gold as the threat does.
//
// The gauge is `remaining / total`, where remaining counts enemies not yet released by the spawners
// as well as the ones on the field — so it only ever falls (see ember-wave-manager.js, which owns
// those numbers). A head count would climb while the player fought, which reads backwards.

const FACE_COLOR = 0x1b1512 // the ember UI's panel brown (command wheel, role picker, speech bubble)
const FACE_ALPHA = 0.72
const GROOVE_COLOR = 0x000000
const GROOVE_ALPHA = 0.32
const RIM_COLOR = 0xffffff
const RIM_ALPHA = 0.1

// Gauge ramp: a full ring is hostile red, a nearly-cleared one is ember gold.
const THREAT_COLOR = 0xef4444
const CLEARING_COLOR = 0xffb648

const COUNT_COLOR = 0xffffff
const CAPTION_COLOR = 0xffc89a
const CHIP_FILL = 0x2a1d16
const CHIP_BORDER = 0xff8a3d
const CHIP_TEXT_COLOR = 0xffd9a8

const COUNT_SIZE = TEXT_ROLES.heading.fontSize
const CAPTION_SIZE = TEXT_ROLES.compactCaption.fontSize
const CHIP_TEXT_SIZE = TEXT_ROLES.compactBody.fontSize
const CHIP_PAD_X = 9
const CHIP_PAD_Y = 4
const CHIP_GAP = 11 // between the dial's outer edge and the top of the chip

const APPEAR_MS = 220
const FLASH_MS = 320
// Below this many enemies the ring breathes — the last of a wave is the part worth watching.
const FINAL_STRETCH = 3

export interface WaveStatus {
    // 1-based wave being fought. Zero (or a null status) means no wave: take the badge down.
    wave: number
    // Enemies left to put down, including any the spawners haven't released yet.
    remaining: number
    // Size of the whole wave; the gauge's denominator.
    total: number
    // Enemies on the field right now. Not drawn — kept so the shape matches what the server sends.
    alive?: number
}

// How full the gauge ring is. Guards the two ways the numbers can arrive unusable: no total yet
// (show a full ring rather than an empty one — the wave has just landed, not been cleared) and a
// remaining count that outruns the total (a late spawn), which would otherwise wrap the arc.
export const gaugeProgress = (remaining: number, total: number): number => {
    if (!(total > 0)) {
        return remaining > 0 ? 1 : 0
    }
    return Math.max(0, Math.min(1, remaining / total))
}

// Red at full, ember gold as it empties.
export const threatColor = (progress: number): number => interpolateColor(CLEARING_COLOR, THREAT_COLOR, progress)

const interpolateColor = (from: number, to: number, factor: number): number => {
    const f = Math.max(0, Math.min(1, factor))
    const r = Math.round(((from >> 16) & 0xff) + (((to >> 16) & 0xff) - ((from >> 16) & 0xff)) * f)
    const g = Math.round(((from >> 8) & 0xff) + (((to >> 8) & 0xff) - ((from >> 8) & 0xff)) * f)
    const b = Math.round((from & 0xff) + ((to & 0xff) - (from & 0xff)) * f)
    return (r << 16) | (g << 8) | b
}

const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3)

export interface WaveIndicatorProps {
    radius?: number
    strokeWidth?: number
}

export class WaveIndicator extends Container {
    private radius: number
    private strokeWidth: number

    private face: Graphics
    private gauge: Graphics
    private chip: Graphics
    private countText: Text
    private captionText: Text
    private chipText: Text

    // What's being displayed. Null until the first wave status arrives.
    private status: WaveStatus | null = null
    // Whether the badge wants to be on screen; `appear` is how far the entrance/exit has got (0..1).
    private wanted = false
    private appear = 0
    // Kill feedback: driven to 1 when `remaining` drops, decays back to 0.
    private flash = 0
    // Free-running clock for the final-stretch breathing.
    private pulseTs = 0
    private needsDraw = true

    constructor(props: WaveIndicatorProps = {}) {
        super()

        this.radius = props.radius || 50
        this.strokeWidth = props.strokeWidth || 7.5

        this.face = new Graphics()
        this.gauge = new Graphics()
        this.chip = new Graphics()

        this.countText = new TextRenderer().color(COUNT_COLOR).fontSize(COUNT_SIZE).bold().align("center").render("0") as Text
        this.countText.anchor.set(0.5)
        this.captionText = new TextRenderer()
            .color(CAPTION_COLOR)
            .fontSize(CAPTION_SIZE)
            .bold()
            .align("center")
            .render("LEFT") as Text
        this.captionText.anchor.set(0.5)
        this.captionText.alpha = 0.75
        this.chipText = new TextRenderer()
            .color(CHIP_TEXT_COLOR)
            .fontSize(CHIP_TEXT_SIZE)
            .bold()
            .align("center")
            .render("WAVE 1") as Text
        this.chipText.anchor.set(0.5)

        this.addChild(this.face)
        this.addChild(this.gauge)
        this.addChild(this.countText)
        this.addChild(this.captionText)
        this.addChild(this.chip)
        this.addChild(this.chipText)

        this.drawFace()
        this.layoutText()

        this.visible = false
        this.alpha = 0
    }

    // Outer painted size of the dial — what a caller positions against (the chip hangs below it and
    // is narrower, so it never widens the badge's footprint).
    public get dialSize(): number {
        return this.radius * 2 + this.strokeWidth
    }

    // How far below the dial's CENTRE the badge actually paints, chip included. Lets a caller keep
    // the whole thing on screen without reaching into the layout.
    public get bottomExtent(): number {
        return this.radius + this.strokeWidth / 2 + CHIP_GAP + CHIP_TEXT_SIZE + CHIP_PAD_Y * 2
    }

    // New wave state from the server, or null / wave 0 to take the badge down.
    public update(status: WaveStatus | null) {
        const live = status && status.wave > 0 ? status : null

        if (!live) {
            if (this.wanted) {
                this.wanted = false
            }
            return
        }

        const previous = this.status
        // A drop in the count is a kill (or several): flash the number so the player sees it land.
        // A new wave is not a drop, however much the number falls between waves.
        if (previous && previous.wave === live.wave && live.remaining < previous.remaining) {
            this.flash = 1
        }

        this.status = live
        this.wanted = true
        this.needsDraw = true
    }

    // Per-frame tick. Only redraws when something is actually moving, so a long wave doesn't repaint
    // a static dial 60 times a second.
    public animate(deltaMs: number) {
        const target = this.wanted ? 1 : 0
        if (this.appear !== target) {
            const step = deltaMs / APPEAR_MS
            this.appear = target > this.appear ? Math.min(1, this.appear + step) : Math.max(0, this.appear - step)
            this.needsDraw = true
        }

        if (this.flash > 0) {
            this.flash = Math.max(0, this.flash - deltaMs / FLASH_MS)
            this.needsDraw = true
        }

        if (this.isBreathing()) {
            this.pulseTs += deltaMs
            this.needsDraw = true
        }

        if (!this.needsDraw) {
            return
        }
        this.needsDraw = false
        this.redraw()
    }

    // Whether the badge is on screen at all (mid-fade counts).
    public isShowing(): boolean {
        return this.visible
    }

    // Test/inspection seam: how strong the kill flash is right now (1 the frame a kill lands, 0 once
    // it has decayed).
    public flashStrength(): number {
        return this.flash
    }

    private isBreathing(): boolean {
        return this.wanted && !!this.status && this.status.remaining > 0 && this.status.remaining <= FINAL_STRETCH
    }

    private redraw() {
        this.visible = this.appear > 0
        if (!this.visible || !this.status) {
            this.alpha = 0
            return
        }

        const eased = easeOut(this.appear)
        this.alpha = eased
        // Grows into place rather than popping — the countdown dial it replaces has just vanished
        // from the same spot.
        this.scale.set(0.82 + 0.18 * eased)

        const { remaining, total, wave } = this.status

        if (this.countText.text !== String(remaining)) {
            this.countText.text = String(remaining)
        }
        const chipLabel = `WAVE ${wave}`
        if (this.chipText.text !== chipLabel) {
            this.chipText.text = chipLabel
            this.layoutChip()
        }

        // The number swells on a kill and settles back.
        this.countText.scale.set(1 + 0.28 * this.flash)

        this.drawGauge(gaugeProgress(remaining, total))
    }

    private drawFace() {
        const arcRadius = this.radius - this.strokeWidth / 2
        const faceRadius = this.radius + this.strokeWidth / 2

        this.face.clear()
        // Dark dial face: depth, and it keeps the number legible over a bright, busy camp.
        this.face.beginFill(FACE_COLOR, FACE_ALPHA)
        this.face.drawCircle(0, 0, faceRadius)
        this.face.endFill()
        // The empty groove the gauge drains along.
        this.face.lineStyle({ width: this.strokeWidth, color: GROOVE_COLOR, alpha: GROOVE_ALPHA, cap: LINE_CAP.ROUND })
        this.face.drawCircle(0, 0, arcRadius)
        // Thin bright rim for a crisp edge.
        this.face.lineStyle({ width: 1.5, color: RIM_COLOR, alpha: RIM_ALPHA })
        this.face.drawCircle(0, 0, faceRadius - 1)
    }

    private layoutText() {
        // The number sits a little above centre to leave room for its caption underneath.
        this.countText.y = -7
        this.captionText.y = 15
        this.layoutChip()
    }

    private layoutChip() {
        const w = this.chipText.width + CHIP_PAD_X * 2
        const h = this.chipText.height + CHIP_PAD_Y * 2
        const top = this.radius + this.strokeWidth / 2 + CHIP_GAP

        this.chipText.y = top + h / 2

        this.chip.clear()
        this.chip.beginFill(CHIP_FILL, 0.92)
        this.chip.lineStyle({ width: 1, color: CHIP_BORDER, alpha: 0.55 })
        this.chip.drawRoundedRect(-w / 2, top, w, h, h / 2)
        this.chip.endFill()
    }

    private drawGauge(progress: number) {
        this.gauge.clear()
        if (progress <= 0) {
            return
        }

        const arcRadius = this.radius - this.strokeWidth / 2
        const color = threatColor(progress)

        // Sweeps clockwise from 12 o'clock and shrinks back toward it as the wave is cleared —
        // the same motion as the countdown dial this badge replaces.
        const startAngle = -Math.PI / 2
        const endAngle = startAngle + progress * Math.PI * 2
        const startX = Math.cos(startAngle) * arcRadius
        const startY = Math.sin(startAngle) * arcRadius

        // Soft outer glow: a fatter, fainter copy of the arc. Cheap — no filters, which the low-end
        // hardware this targets can't spare.
        const breath = this.isBreathing() ? 0.5 + 0.5 * Math.sin(this.pulseTs / 190) : 0
        const haloAlpha = 0.18 + 0.22 * breath + 0.3 * this.flash
        this.gauge.lineStyle({
            width: this.strokeWidth + 5 + 4 * breath,
            color,
            alpha: haloAlpha,
            cap: LINE_CAP.ROUND,
        })
        this.gauge.moveTo(startX, startY)
        this.gauge.arc(0, 0, arcRadius, startAngle, endAngle)

        // Main arc. A kill washes it toward white for a moment.
        this.gauge.lineStyle({
            width: this.strokeWidth,
            color: this.flash > 0 ? interpolateColor(color, 0xffffff, this.flash * 0.55) : color,
            alpha: 1,
            cap: LINE_CAP.ROUND,
        })
        this.gauge.moveTo(startX, startY)
        this.gauge.arc(0, 0, arcRadius, startAngle, endAngle)

        // Bright bead at the draining end, so the eye can find how far round the gauge has got.
        const tipX = Math.cos(endAngle) * arcRadius
        const tipY = Math.sin(endAngle) * arcRadius
        this.gauge.lineStyle(0)
        this.gauge.beginFill(0xffffff, 0.85)
        this.gauge.drawCircle(tipX, tipY, this.strokeWidth * 0.3)
        this.gauge.endFill()
    }
}
